import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import PaymentModal from "../../components/UI/PaymentModal";
import SubscriptionService from "../../services/SubscriptionService";
import analyticsService from "../../services/AnalyticsService";
import { useIsMobile } from "../../App";

const ProfileScreen = () => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [editedData, setEditedData] = useState({
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [subscription, setSubscription] = useState({});
  const [paymentResponse, setPaymentResponse] = useState({});
  const [buyModal, setBuyModal] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [cancelOptions, setCancelOptions] = useState([]);
  const [cancelReasons, setCancelReasons] = useState({});
  const [otherReasonText, setOtherReasonText] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [cancelSuccess, setCancelSuccess] = useState("");
  const [loadingCancelOptions, setLoadingCancelOptions] = useState(false);
  const [showPostCancelInfoModal, setShowPostCancelInfoModal] = useState(false);

  const { user, updateUser, deleteAccount, getUserInfo, changePassword } =
    useAuth();

  const { theme } = useTheme();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { profileData } = await getUserInfo();
        const subscriptionData =
          await SubscriptionService.getSubscriptionInfo();

        setSubscription(subscriptionData);
        setUserInfo(profileData);
      } catch (error) {
        setError(error);
      }
    };

    fetchUserInfo();
  }, []);

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
    } catch (error) {
      setError(error);
    }
  };

  const handleEdit = () => {
    setEditedData({
      email: user?.email || "",
      phone: user?.phone || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setError("");
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setError("");
      // Name is fixed and not editable
      const payload = {
        // add other editable fields if needed (e.g., phone)
      };
      await updateUser(payload);
      setIsEditing(false);
    } catch (error) {
      setError(error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
  };

  const handleChangePassword = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setIsChangingPassword(true);
  };

  const handleSavePassword = async () => {
    try {
      setPasswordError("");

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError(t("passwordsDontMatch"));
        return;
      }
      if (!passwordData.currentPassword) {
        setPasswordError(t("enterCurrentPassword"));
        return;
      }
      if (
        passwordData.newPassword.length < 8 ||
        passwordData.confirmPassword.length < 8
      ) {
        setPasswordError(t("passwordTooShort"));
        return;
      }

      // Use the new changePassword method
      const result = await changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
      });
      if (!result.success) {
        if (result.error.includes("Current password is incorrect")) {
          setPasswordError(t("currentPasswordIncorrect"));
        } else {
          setPasswordError(result.error);
        }
        return;
      } else {
        setPasswordSuccess(t("passwordChangedSuccessfully"));
        setIsChangingPassword(false);
      }

      setIsChangingPassword(false);
    } catch (error) {
      setPasswordError(error.message);
    }
  };

  const handleCancelPassword = () => {
    setIsChangingPassword(false);
    setPasswordError("");
  };

  const handleResetPassword = () => {
    navigate("/forgot-password");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;

    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBuySubscription = async () => {
    navigate("/subscription");
  };

  const handleCancelSubscriptionClick = async () => {
    setShowCancelSubscriptionModal(true);
    setCancelError("");
    setCancelSuccess("");
    setOtherReasonText("");
    setLoadingCancelOptions(true);

    try {
      const result = await SubscriptionService.getCancelOptions();
      console.log("Cancel options result:", result); // Діагностика
      if (result.success && result.data) {
        // Очікуємо, що API повертає масив опцій
        // Можливі формати: [{id, name, is_other}, ...] або [{key, label, requires_text}, ...]
        const options = Array.isArray(result.data) ? result.data : (result.data.options || []);
        console.log("Parsed options:", options); // Діагностика
        
        // Додаємо унікальний ключ до кожної опції, якщо його немає
        const optionsWithKeys = options.map((option, index) => {
          // API повертає code, тому використовуємо його як ключ
          const key = option.code || option.id || option.key || option.value || `option_${index}`;
          return {
            ...option,
            _uniqueKey: key, // Зберігаємо унікальний ключ
          };
        });
        
        // Додаємо опцію "Інше" вручну
        const otherOption = {
          code: "other",
          label: t("other"),
          _uniqueKey: "other",
          is_other: true,
          _isManual: true, // Позначаємо, що це ручна опція
        };
        
        const allOptions = [...optionsWithKeys, otherOption];
        setCancelOptions(allOptions);
        
        // Ініціалізуємо стан чекбоксів
        const initialReasons = {};
        allOptions.forEach((option) => {
          initialReasons[option._uniqueKey] = false;
        });
        console.log("Initial reasons state:", initialReasons); // Діагностика
        setCancelReasons(initialReasons);
      } else {
        setCancelError(result.error || t("failedToLoadCancelOptions"));
      }
    } catch (error) {
      console.error("Error loading cancel options:", error); // Діагностика
      setCancelError(error.message || t("errorLoadingOptions"));
    } finally {
      setLoadingCancelOptions(false);
    }
  };

  const handleCancelReasonChange = (reasonKey) => {
    console.log("Changing reason:", reasonKey, "Current state:", cancelReasons); // Діагностика
    setCancelReasons((prev) => {
      const currentValue = prev[reasonKey] || false;
      const newValue = !currentValue;

      const option = cancelOptions.find((opt) => opt._uniqueKey === reasonKey);
      const isOther =
        option?.is_other ||
        option?.requires_text ||
        option?.isOther ||
        option?.code === "other" ||
        option?._isManual === true;

      const resetState = {};
      cancelOptions.forEach((opt) => {
        const key =
          opt._uniqueKey || opt.code || opt.id || opt.key || opt.value;
        resetState[key] = false;
      });

      if (newValue) {
        resetState[reasonKey] = true;
      }

      if (!newValue || !isOther) {
        setOtherReasonText("");
      }

      console.log("Updated state:", resetState); // Діагностика
      return resetState;
    });
  };

  const handleCancelSubscription = async () => {
    let selectedReasonCode = null;
    let needsCustomReason = false;

    try {
      setCancelError("");
      setCancelSuccess("");

      const customReasonText = otherReasonText.trim();
      
      cancelOptions.some((option) => {
        const key =
          option._uniqueKey ||
          option.code ||
          option.id ||
          option.key ||
          option.value;
        if (cancelReasons[key] === true) {
          const isOther =
            option.is_other ||
            option.requires_text ||
            option.isOther ||
            option.code === "other" ||
            option._isManual === true;

          if (isOther) {
            selectedReasonCode = null;
            needsCustomReason = true;
          } else {
            selectedReasonCode =
              option.code ||
              option.id ||
              option.key ||
              option.value ||
              key;
          }
          return true;
        }
        return false;
      });

      if (!selectedReasonCode && !needsCustomReason) {
        setCancelError(t("selectAtLeastOneReason"));
        return;
      }

      if (needsCustomReason && !customReasonText) {
        setCancelError(t("enterOtherReason"));
        return;
      }

      try {
        analyticsService.logEvent("subscription_cancel_request_web", {
          reason_code: needsCustomReason ? null : selectedReasonCode,
          has_custom_reason: needsCustomReason,
          timestamp: new Date().toISOString(),
        });
      } catch (_) {}

      const result = await SubscriptionService.cancelSubscription(
        needsCustomReason ? null : selectedReasonCode,
        needsCustomReason ? customReasonText : null
      );

      if (result.success) {
        setCancelSuccess(t("subscriptionCancelledSuccess"));
        setSubscription({ ...subscription, is_valid: false });
        setShowCancelSubscriptionModal(false);
        setShowPostCancelInfoModal(true);
        setCancelSuccess("");

        try {
          analyticsService.logEvent("subscription_cancel_success_web", {
            reason_code: needsCustomReason ? null : selectedReasonCode,
            has_custom_reason: needsCustomReason,
            timestamp: new Date().toISOString(),
          });
        } catch (_) {}
      } else {
        setCancelError(result.error || t("cancelSubscriptionError"));

        try {
          analyticsService.logEvent("subscription_cancel_error_web", {
            reason_code: needsCustomReason ? null : selectedReasonCode,
            has_custom_reason: needsCustomReason,
            error_message: result.error || "Unknown cancel error",
            timestamp: new Date().toISOString(),
          });
        } catch (_) {}
      }
    } catch (error) {
      setCancelError(error.message || t("cancelSubscriptionError"));

      try {
        analyticsService.logEvent("subscription_cancel_error_web", {
          reason_code: needsCustomReason ? null : selectedReasonCode,
          has_custom_reason: needsCustomReason,
          error_message: error?.message || "Unknown cancel error",
          timestamp: new Date().toISOString(),
        });
      } catch (_) {}
    }
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2) || "?"
    );
  };

  const containerStyle = {
    maxWidth: isMobile ? "100vw" : 800,
    margin: isMobile ? "0" : "auto",
    padding: isMobile ? "12px 4px" : "32px",
    background: theme.surface,
    borderRadius: isMobile ? 8 : 16,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    color: theme.text,
    height: "90vh",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    overflowY: "auto",
  };

  const profileHeaderStyles = {
    textAlign: "center",
    marginBottom: "32px",
    padding: "24px",
    backgroundColor: theme.surface,
    borderRadius: "16px",
    border: `1px solid ${theme.border}`,
  };

  const avatarStyles = {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: theme.primary,
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    fontWeight: "bold",
    margin: "0 auto 16px",
  };

  const nameStyles = {
    fontSize: "24px",
    fontWeight: "bold",
    color: theme.text,
    marginBottom: "8px",
  };

  const emailStyles = {
    fontSize: "16px",
    color: theme.textSecondary,
    marginBottom: "8px",
  };

  const sectionStyles = {
    backgroundColor: theme.surface,
    borderRadius: "12px",
    border: `1px solid ${theme.border}`,
    marginBottom: "20px",
  };

  const sectionHeaderStyles = {
    padding: "16px 20px",
    backgroundColor: theme.isDark ? theme.border : theme.background,
    borderBottom: `1px solid ${theme.border}`,
    borderRadius: "12px",
    fontSize: "18px",
    fontWeight: "600",
    color: theme.text,
  };

  const modalOverlayStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalStyles = {
    backgroundColor: theme.surface,
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "500px",
    width: "90%",
    border: `1px solid ${theme.border}`,
  };

  const modalTitleStyles = {
    fontSize: "20px",
    fontWeight: "bold",
    color: theme.text,
    marginBottom: "16px",
  };

  const modalTextStyles = {
    fontSize: "16px",
    color: theme.textSecondary,
    marginBottom: "24px",
  };

  const modalButtonsStyles = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    marginBottom: "12px",
  };

  const successStyles = {
    color: theme.success || "#28a745",
    fontSize: "14px",
    marginBottom: "12px",
    textAlign: "center",
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          .profile-btn {
            border-radius: 16px !important;
            font-size: 18px !important;
            font-weight: 600 !important;
            width: 100% !important;
            padding: 16px 0 !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            transition: all 0.2s;
            margin-bottom: 12px !important;
          }
          
          .profile-btn:last-child {
            margin-bottom: 0 !important;
          }

          .profile-btn-primary {
            background: ${theme.primary} !important;
            color: #fff !important;
            border: none !important;
          }
          
          .profile-btn-primary:hover {
            background: ${theme.primaryHover || theme.primary} !important;
          }

          .profile-btn-danger {
            background: #dc3545 !important;
            color: #fff !important;
            border: none !important;
          }
          
          .profile-btn-danger:hover {
            background: #c82333 !important;
          }

          .profile-btn-secondary {
            background: #6c757d !important;
            color: #fff !important;
            border: none !important;
          }
          
          .profile-btn-secondary:hover {
            background: #5a6268 !important;
          }

          .button-group {
            display: flex !important;
            gap: 12px !important;
            margin-top: 16px !important;
          }

          .button-group .profile-btn {
            flex: 1 !important;
            margin-bottom: 0 !important;
          }
        `}
      </style>
      {/* Profile Header */}
      <div style={profileHeaderStyles}>
        <div style={avatarStyles}>
          {getInitials('Nachmed.AI')}
        </div>
        <div style={nameStyles}>
          {'Nachmed.AI'}
        </div>
        <div style={emailStyles}>
          {userInfo ? userInfo?.email : user?.email}
        </div>
      </div>
      {/* Subscription Section */}
      <div style={sectionStyles}>
        <div style={sectionHeaderStyles}>{t("subscription") || "Підписка"}</div>
        <div style={{ padding: "20px", display: "grid", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("subscriptionStatus") || "Статус"}</span>
            <span style={{ color: theme.textSecondary }}>
              {subscription && subscription?.is_valid
                ? t("active")
                : t("inactive")}
            </span>
          </div>
          {subscription && subscription?.is_valid ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>{t("subscriptionEnds") || "Дата закінчення"}</span>
                <span style={{ color: theme.textSecondary }}>
                  {(subscription && subscription?.expiration_date) || "—"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>{t("requestsLeft") || "Залишилось запитів"}</span>
                <span style={{ color: theme.textSecondary }}>
                  {(subscription && subscription?.questions_remaining) || "—"}
                </span>
              </div>
              {subscription?.is_recurrent !== false && (
                <div
                  style={{
                    marginTop: "12px",
                    textAlign: "center",
                  }}
                >
                  <button
                    onClick={handleCancelSubscriptionClick}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.textSecondary,
                      fontSize: "14px",
                      textDecoration: "underline",
                      cursor: "pointer",
                      padding: "4px 8px",
                      opacity: 0.7,
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "1")}
                    onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                  >
                    {t("cancelSubscription")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ padding: "8px 0", color: theme.text }}>
                {t("needSubscription") || "Потрібна підписка"}
              </div>
              <Button
                onClick={handleBuySubscription}
                variant="primary"
                className="profile-btn profile-btn-primary"
                icon={
                  <svg
                    style={{ marginTop: "5px" }}
                    width="25px"
                    height="25px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M17 11C16.4477 11 16 10.5523 16 10C16 9.4477 16.4477 9 17 9H19C19.5523 9 20 9.4477 20 10C20 10.5523 19.5523 11 19 11H17zM8 11C7.44772 11 7 10.5523 7 10C7 9.4477 7.44772 9 8 9H14C14.5523 9 15 9.4477 15 10C15 10.5523 14.5523 11 14 11H8zM22 5V3C22 2.44772 21.5523 2 21 2H3C2.44772 2 2 2.44772 2 3V5H22zM22 7H2V15C2 15.5523 2.44772 16 3 16H21C21.5523 16 22 15.5523 22 15V7zM3 0H21C22.6569 0 24 1.34315 0 3V15C24 16.6569 22.6569 18 21 18H3C1.34315 18 0 16.6569 0 15V3C0 1.34315 1.34315 0 3 0z"
                        fill="white"
                      ></path>
                    </g>
                  </svg>
                }
              >
                {t("buySubscription") || "Купити підписку"}
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Profile sections area */}
      <div>
        {/* Edit Profile Section */}
        <div style={sectionStyles}>
          <div style={sectionHeaderStyles}>{t("editProfile")}</div>

          {isEditing ? (
            <div style={{ padding: "20px" }}>
              {error && <div style={errorStyles}>{error}</div>}

              {/* Email field - read only */}
              <div style={{ 
                padding: "12px 16px", 
                backgroundColor: theme.background, 
                border: `1px solid ${theme.border}`, 
                borderRadius: "8px", 
                color: theme.textSecondary,
                fontSize: "16px",
                marginBottom: "16px"
              }}>
                <div style={{ fontSize: "14px", color: theme.textSecondary, marginBottom: "4px" }}>
                  {t("email")}
                </div>
                <div style={{ color: theme.text }}>
                  {editedData.email}
                </div>
              </div>

              <div className="button-group">
                <Button
                  onClick={handleSave}
                  variant="primary"
                  className="profile-btn profile-btn-primary"
                >
                  {t("save")}
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="secondary"
                  className="profile-btn profile-btn-secondary"
                >
                  {t("cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ padding: "20px" }}>
              <Button
                onClick={handleEdit}
                variant="primary"
                className="profile-btn profile-btn-primary"
              >
                {t("editProfileButton")}
              </Button>
            </div>
          )}
        </div>

        {/* Change Password Section */}
        <div style={sectionStyles}>
          <div style={sectionHeaderStyles}>{t("changePassword")}</div>

          {isChangingPassword ? (
            <div style={{ padding: "20px" }}>
              {passwordError && <div style={errorStyles}>{passwordError}</div>}
              {passwordSuccess && (
                <div style={successStyles}>{passwordSuccess}</div>
              )}

              <Input
                type="password"
                className="profile"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordInputChange}
                placeholder={t("currentPassword")}
              />

              <Input
                type="password"
                className="profile"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordInputChange}
                placeholder={t("newPassword")}
              />

              <Input
                type="password"
                className="profile"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordInputChange}
                placeholder={t("confirmNewPassword")}
              />

              <div className="button-group">
                <Button
                  onClick={handleSavePassword}
                  variant="primary"
                  className="profile-btn profile-btn-primary"
                >
                  {t("save")}
                </Button>
                <Button
                  onClick={handleCancelPassword}
                  variant="secondary"
                  className="profile-btn profile-btn-secondary"
                >
                  {t("cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ padding: "20px" }}>
              <Button
                onClick={handleChangePassword}
                variant="primary"
                fullWidth
                className="profile-btn profile-btn-primary"
              >
                {t("changePasswordButton")}
              </Button>
            </div>
          )}
        </div>

        {/* Account Actions */}
        <div style={sectionStyles}>
          <div style={{ padding: "20px" }}>
            <Button
              onClick={() => setShowConfirmDelete(true)}
              variant="danger"
              size={isMobile ? "small" : "large"}
              fullWidth
              className="profile-btn profile-btn-danger"
            >
              {t("deleteAccount")}
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showConfirmDelete && (
        <div
          style={modalOverlayStyles}
          onClick={() => setShowConfirmDelete(false)}
        >
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyles}>{t("deleteAccountConfirmation")}</div>
            <div style={modalTextStyles}>
              {t("deleteAccountConfirmationDesc")}
            </div>
            <div style={modalButtonsStyles}>
              <Button
                onClick={() => setShowConfirmDelete(false)}
                variant="secondary"
                className="profile-btn profile-btn-secondary"
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleDeleteAccount}
                variant="danger"
                className="profile-btn profile-btn-danger"
              >
                {t("delete")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Post cancellation info */}
      {showPostCancelInfoModal && (
        <div
          style={modalOverlayStyles}
          onClick={() => setShowPostCancelInfoModal(false)}
        >
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyles}>
              {t("subscriptionCancellationInfoTitle") ||
                "Підписку скасовано"}
            </div>
            <div style={modalTextStyles}>
              {t("subscriptionCancellationInfoMessage") ||
                "Якщо у вас залишились доступні запити або термін підписки ще не завершився, ви можете користуватися сервісом до кінця оплаченого періоду."}
            </div>
            <div style={modalButtonsStyles}>
              <Button
                onClick={() => setShowPostCancelInfoModal(false)}
                variant="primary"
                className="profile-btn profile-btn-primary"
              >
                {t("gotIt") || "Зрозуміло"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Buy subscription confirmation */}
      {buyModal && (
        <PaymentModal
          open={buyModal}
          onClose={() => setBuyModal(false)}
          paymentResponse={paymentResponse}
        />
      )}

      {/* Cancel Subscription Modal */}
      {showCancelSubscriptionModal && (
        <div
          style={modalOverlayStyles}
          onClick={() => setShowCancelSubscriptionModal(false)}
        >
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyles}>{t("cancelSubscriptionTitle")}</div>
            <div style={modalTextStyles}>
              {t("cancelSubscriptionDesc")}
            </div>
            
            {cancelError && <div style={errorStyles}>{cancelError}</div>}
            {cancelSuccess && <div style={successStyles}>{cancelSuccess}</div>}

            {loadingCancelOptions ? (
              <div style={{ textAlign: "center", padding: "20px", color: theme.textSecondary }}>
                {t("loadingCancelOptions")}
              </div>
            ) : (
              <div style={{ marginBottom: "20px" }}>
                {cancelOptions.length === 0 ? (
                  <div style={{ color: theme.textSecondary, padding: "20px", textAlign: "center" }}>
                    {t("cancelOptionsUnavailable")}
                  </div>
                ) : (
                  cancelOptions.map((option, index) => {
                    const key = option._uniqueKey || option.code || option.id || option.key || option.value || `option_${index}`;
                    const label = option.label || option.name || option.title || option.text || "Без назви";
                    const isOther = option.is_other || option.requires_text || option.isOther || option.code === "other" || option._isManual === true || false;
                    const isChecked = cancelReasons[key] === true; // Використовуємо строгу перевірку

                    return (
                      <div key={key}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "12px",
                            cursor: "pointer",
                            color: theme.text,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleCancelReasonChange(key)}
                            style={{
                              marginRight: "8px",
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                            }}
                          />
                          <span>{label}</span>
                        </label>

                        {isOther && isChecked && (
                          <div style={{ marginTop: "8px", marginLeft: "26px", marginBottom: "12px" }}>
                            <Input
                              type="text"
                              className="profile"
                              name="otherReason"
                              value={otherReasonText}
                              onChange={(e) => setOtherReasonText(e.target.value)}
                              placeholder={t("specifyReason")}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <div style={modalButtonsStyles}>
              <Button
                onClick={() => setShowCancelSubscriptionModal(false)}
                variant="secondary"
                className="profile-btn profile-btn-secondary"
              >
                {t("cancel") || "Скасувати"}
              </Button>
              <Button
                onClick={handleCancelSubscription}
                variant="danger"
                className="profile-btn profile-btn-danger"
                disabled={!!cancelSuccess}
              >
                {t("cancelSubscription")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
